import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME, UBICACIONES_SECCIONES_VALIDAS } from '../../config/constants';
import Slider from 'react-slick';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const MainCarousel = () => {
  const [images, setImages] = useState([]);
  const [clickableImages, setClickableImages] = useState({});
  const [documentInfos, setDocumentInfos] = useState({}); 
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  // Debounce function for resize events
  const debounce = (func, wait) => {
    let timeout;
    return function () {
      const context = this, args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/getDocumentosMainCarousel`);
        const documentos = response.data.documentos;
  
        const clickableState = {};
        const docInfos = {}; // Object que guarda toda la info de los documentos
  
        const imagesWithDates = documentos.map((doc) => {
          clickableState[doc.id] = doc.ubicacion !== "Ninguna";
          docInfos[doc.id] = {
            ubicacion: doc.ubicacion,
            ruta: doc.ruta
          };
          return {
            id: doc.id,
            fechaCreacion: doc.fechaCreacion ? new Date(doc.fechaCreacion) : null,
          };
        });
        setClickableImages(clickableState);
        setDocumentInfos(docInfos);
        setImages(sortCarousel(imagesWithDates));
        setLoading(false);
      } catch (error) {
        console.error('Error obteniendo imagenes para el carousel');
        setLoading(false);
      }
    };
    fetchImages();
    // Preconnect to Cloudinary for faster image loading
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = `https://res.cloudinary.com`;
    preconnect.crossOrigin = 'true';
    document.head.appendChild(preconnect);
  
    return () => {
      document.head.removeChild(preconnect);
    };
  }, []);

  /* Controla el orden de las imagenes del carousel */
  const sortCarousel = (items) => {
    const fixedId = "yh7bewip9aocflbd3egh"; // ID del componente inicial
    const clonedItems = [...items];

    const fixedItem = clonedItems.find(item => item.id === fixedId);
    const otherItems = clonedItems
      .filter(item => item.id !== fixedId)
      .sort((a, b) => {
        const dateA = a.fechaCreacion ? new Date(a.fechaCreacion) : 0;
        const dateB = b.fechaCreacion ? new Date(b.fechaCreacion) : 0;
        return dateB - dateA;
      });

    return fixedItem ? [fixedItem, ...otherItems] : otherItems;
  };

  /* Checkea si es dispositivo movil con debounce */
  useEffect(() => {
    const checkIfMobile = () => {
      const mobileCheck = window.innerWidth <= 768;
      if (mobileCheck !== isMobile) {
        setIsMobile(mobileCheck);
      }
    };

    const debouncedCheck = debounce(checkIfMobile, 250);
    checkIfMobile();
    window.addEventListener("resize", debouncedCheck);
    return () => window.removeEventListener("resize", debouncedCheck);
  }, [isMobile]);

  const getImageUrl = (publicId, quality = 'auto', width = 'auto') => {
    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/f_auto,q_${quality},w_${width}/${publicId}`;
  };

  const scrollToSection = (id) => {
    const section = document.querySelector(id);
    if (section) {
      const offset = 70;
      const sectionPosition = section.offsetTop - offset;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth',
      });
    }
  };

  const onClickItem = (publicId) => {
    const docInfo = documentInfos[publicId];
    if (!docInfo) {
      console.error('Document info not found for:', publicId);
      return;
    }

    if (docInfo.ubicacion === UBICACIONES_SECCIONES_VALIDAS.NAVBAR) {
      const redirectUrl = `#${docInfo.ruta}`.replace(/\s+/g, '');
      scrollToSection(redirectUrl);
    } else {
      window.location.href = `/${docInfo.ruta}`;
    }
  };

  const PrevArrow = ({ onClick, isMobile }) => {
    if (isMobile) return null;
    const arrowSize = Math.min(150, window.innerWidth * 0.025);

    return (
      <div
        style={{
          display: "block",
          position: "absolute",
          top: "50%",
          left: "20px",
          zIndex: 1000,
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        <svg
          width={arrowSize}
          height={arrowSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 16L6 10L12 4"
            stroke="#e3c6c4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  const NextArrow = ({ onClick, isMobile }) => {
    if (isMobile) return null;
    const arrowSize = Math.min(150, window.innerWidth * 0.025);

    return (
      <div
        style={{
          display: "block",
          position: "absolute",
          top: "50%",
          right: "20px",
          zIndex: 1000,
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        <svg
          width={arrowSize}
          height={arrowSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 16L14 10L8 4"
            stroke="#e3c6c4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  const handleTouchStart = () => {
    sliderRef.current?.slickPause();
  };

  const handleTouchEnd = () => {
    sliderRef.current?.slickPlay();
  };

  const handleBeforeChange = (current, next) => {
    const preloadIndexes = [next];
    if (next > 0) preloadIndexes.push(next - 1);
    if (next < images.length - 1) preloadIndexes.push(next + 1);

    preloadIndexes.forEach(index => {
      const img = document.querySelector(`img[data-id="${images[index].id}"]`);
      if (img && !img.src) {
        img.src = img.dataset.src;
      }
    });
  };

  const settings = useMemo(() => ({
    infinite: images.length > 1,
    speed: 700,
    autoplay: images.length > 1,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: true,
    draggable: false,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    lazyLoad: 'progressive',
    initialSlide: 0,
    cssEase: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    beforeChange: handleBeforeChange,
    responsive: [
      {
        breakpoint: 1920,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    appendDots: dots => (
      <div
        style={{
          position: "absolute",
          bottom: "0px",
          left: "0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "transparent",
          padding: "10px",
          zIndex: 1000,
        }}
      >
        <ul className='custom-container-dot'>
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <div className='custom-carousel-dot'></div>
    ),
    arrows: !isMobile,
    prevArrow: <PrevArrow isMobile={isMobile} />,
    nextArrow: <NextArrow isMobile={isMobile} />,
  }), [images.length, isMobile]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .slick-dots li.slick-active div {
        opacity: 0.8 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="main-banner" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {loading ? (
        <Skeleton
          className="main-carousel-skeleton"
          height="80vh"
          containerTestId="carousel-loading"
          baseColor="#fff"
          highlightColor="#fffaf2"
        />
      ) : (
        <Slider
          ref={sliderRef}
          {...settings}
          key={`carousel-${images.length}`}
        >
          {images.map((image, index) => (
            <div key={image.id} className="carousel-slide-container">
              <Skeleton
                className="image-skeleton"
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                }}
                height="100%"
                containerTestId={`image-loading-${image.id}`}
                baseColor="#fff"
                highlightColor="#fffaf2"
              />
              <img
                src={getImageUrl(image.id, 'auto', 'auto')}
                alt={`imagen-banner-${image.id}`}
                className={`carousel-image ${clickableImages[image.id] ? 'clickable-wrapper' : 'unclickable-wrapper'}`}
                onClick={() => clickableImages[image.id] && onClickItem(image.id)}
                loading={index < 2 ? 'eager' : 'lazy'}
                data-id={image.id}
                onLoad={(e) => {
                  const skeleton = e.target.parentElement.querySelector('.image-skeleton');
                  if (skeleton) {
                    skeleton.style.display = 'none';
                  }
                }}
                onError={(e) => {
                  const skeleton = e.target.parentElement.querySelector('.image-skeleton');
                  if (skeleton) {
                    skeleton.style.display = 'none';
                  }
                }}
                style={{
                  position: 'relative',
                  zIndex: 2,
                }}
              />
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default MainCarousel;

/*
import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME, UBICACIONES_SECCIONES_VALIDAS } from '../../config/constants';
import Slider from 'react-slick';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const MainCarousel = () => {
  const [images, setImages] = useState([]);
  const [clickableImages, setClickableImages] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/getDocumentosMainCarousel`);
        const documentos = response.data.documentos;

        const imagesWithDates = documentos.map(doc => ({
          id: doc.id,
          fechaCreacion: doc.fechaCreacion ? new Date(doc.fechaCreacion) : null,
        }));

        const clickableState = {};
        for (const doc of documentos) {
          const response = await axios.post(`${API_BASE_URL}/getInfoDocumento`, {
            collectionName: 'maincarousel',
            docId: doc.id
          });
          clickableState[doc.id] = response.data.doc_info.ubicacion !== "Ninguna";
        }

        setClickableImages(clickableState);
        setImages(sortCarousel(imagesWithDates));
        setLoading(false);
      } catch (error) {
        console.error('Error obteniendo imagenes para el carousel');
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  /* Controla el orden de las imagenes del carousel */
  /*
  const sortCarousel = (items) => {
    const fixedId = "mwid4hawhuhdxtuthalj"; // ID del componente inicial
    const clonedItems = [...items];

    const fixedItem = clonedItems.find(item => item.id === fixedId);
    const otherItems = clonedItems
      .filter(item => item.id !== fixedId)
      .sort((a, b) => {
        const dateA = a.fechaCreacion ? new Date(a.fechaCreacion) : 0;
        const dateB = b.fechaCreacion ? new Date(b.fechaCreacion) : 0;
        return dateB - dateA;
      });

    return fixedItem ? [fixedItem, ...otherItems] : otherItems;
  };

  /* Checkea si es dispositivo movil */
  /*
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const getImageUrl = (publicId) => {
    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/f_auto/${publicId}`;
  };

  const scrollToSection = (id) => {
    const section = document.querySelector(id);
    if (section) {
      const offset = 70;
      const sectionPosition = section.offsetTop - offset;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth',
      });
    }
  };

  const onClickItem = async (publicId) => {
    const response = await axios.post(`${API_BASE_URL}/getInfoDocumento`, {
      collectionName: 'maincarousel',
      docId: publicId
    });

    if (response.data.doc_info.ubicacion === UBICACIONES_SECCIONES_VALIDAS.NAVBAR) {
      const redirectUrl = `#${response.data.doc_info.ruta}`.replace(/\s+/g, '');
      scrollToSection(redirectUrl);
    } else {
      window.location.href = `/${response.data.doc_info.ruta}`;
    }
  };

  const PrevArrow = ({ onClick, isMobile }) => {
    if (isMobile) return null;
    const arrowSize = Math.min(150, window.innerWidth * 0.025);

    return (
      <div
        style={{
          display: "block",
          position: "absolute",
          top: "50%",
          left: "20px",
          zIndex: 1000,
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        <svg
          width={arrowSize}
          height={arrowSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 16L6 10L12 4"
            stroke="#e3c6c4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  const NextArrow = ({ onClick, isMobile }) => {
    if (isMobile) return null;
    const arrowSize = Math.min(150, window.innerWidth * 0.025);

    return (
      <div
        style={{
          display: "block",
          position: "absolute",
          top: "50%",
          right: "20px",
          zIndex: 1000,
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={onClick}
      >
        <svg
          width={arrowSize}
          height={arrowSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 16L14 10L8 4"
            stroke="#e3c6c4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  const handleTouchStart = () => {
    sliderRef.current?.slickPause();
  };

  const handleTouchEnd = () => {
    sliderRef.current?.slickPlay();
  };

  const settings = useMemo(() => ({
    infinite: images.length > 1,
    speed: 700,
    autoplay: images.length > 1,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: true,
    draggable: false,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    responsive: [
      {
        breakpoint: 1920,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    appendDots: dots => (
      <div
        style={{
          position: "absolute",
          bottom: "0px",
          left: "0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "transparent",
          padding: "10px",
          zIndex: 1000,
        }}
      >
        <ul className='custom-container-dot'>
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <div className='custom-carousel-dot'></div>
    ),
    arrows: !isMobile,
    prevArrow: <PrevArrow isMobile={isMobile} />,
    nextArrow: <NextArrow isMobile={isMobile} />,
  }), [images.length, isMobile]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .slick-dots li.slick-active div {
        opacity: 0.8 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div className="main-banner" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {loading ? (
        <Skeleton
          className="main-carousel-skeleton"
          height="80vh"
          containerTestId="carousel-loading"
          baseColor="#fff" 
          highlightColor="#fffaf2"
        />
      ) : (
        <Slider
          ref={sliderRef}
          {...settings}
          key={`carousel-${images.length}`}
        >
          {images.map((image) => (
            <div key={image.id}>
              <img
                src={getImageUrl(image.id)}
                alt={`imagen-banner-${image.id}`}
                className={`carousel-image ${clickableImages[image.id] ? 'clickable-wrapper' : 'unclickable-wrapper'}`}
                onClick={() => clickableImages[image.id] && onClickItem(image.id)}
                loading="lazy"
              />
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default MainCarousel;
*/